import type { FieldVisitPlace } from '../../field-operations/types/FieldOperations';

export type ScheduleRuleType =
  | 'clinicOpeningHours'
  | 'clinicClosure'
  | 'roomBlockout'
  | 'staffShift'
  | 'staffTimeOff'
  | 'chatBotBlockout';

/** CamelCase status code from tenant definitions (system + custom). */
export type CalendarEventStatus = string;

export type AppointmentTypeStatus = 'active' | 'archived';

export interface CalendarEventStatusDefinition {
  id: string;
  code: string;
  name: string;
  color?: string;
  sortOrder: number;
  status: AppointmentTypeStatus;
  isSystem: boolean;
  isDefault: boolean;
  countsAsBooking: boolean;
  suppressesReminders: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CalendarEventStatusDefinitionListFilters = {
  status?: AppointmentTypeStatus;
};

export type CreateCalendarEventStatusDefinitionInput = {
  name: string;
  code?: string;
  color?: string;
  sortOrder?: number;
  isDefault?: boolean;
  countsAsBooking?: boolean;
  suppressesReminders?: boolean;
  status?: AppointmentTypeStatus;
};

export type UpdateCalendarEventStatusDefinitionInput = {
  name?: string;
  color?: string;
  sortOrder?: number;
  isDefault?: boolean;
  countsAsBooking?: boolean;
  suppressesReminders?: boolean;
  status?: AppointmentTypeStatus;
};
export type ConsultationCreationMode = 'single' | 'perContact' | 'perAsset' | 'perEmployee';
export type ChatBotBookingFlow = 'offerAvailableSlots' | 'autoNearestSlot';

export type ConsultationFieldType =
  | 'text'
  | 'longText'
  | 'number'
  | 'decimal'
  | 'boolean'
  | 'date'
  | 'time'
  | 'dateTime'
  | 'singleSelect'
  | 'multiSelect';

export interface ConsultationFieldOption {
  id: string;
  label: string;
}

export type DayOfWeek =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export type ScheduleRepeatPattern = 'none' | 'weekly' | 'monthly' | 'yearly';

export type RecurringFrequency = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface CalendarRecurrence {
  frequency: RecurringFrequency;
  interval: number;
  daysOfWeek: DayOfWeek[];
  dayOfMonth?: number;
  endDate?: string;
  maxOccurrences?: number;
}

export type StaffScheduleStatus =
  | 'working'
  | 'management'
  | 'studyLeave'
  | 'holiday'
  | 'sickLeave'
  | 'otherLocation'
  | 'workingFromHome';

export interface ScheduleRule {
  id: string;
  type: ScheduleRuleType;
  scheduleStatus?: StaffScheduleStatus;
  repeatPattern?: ScheduleRepeatPattern;
  isAllDay?: boolean;
  isEnabled: boolean;
  chatBotBlockoutId?: string;
  locationId?: string;
  daysOfWeek?: DayOfWeek[];
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  startAtUtc?: string;
  endAtUtc?: string;
  roomId?: string;
  staffId?: string;
  createdAtUtc?: string;
  updatedAtUtc?: string;
}

export interface ScheduleRuleListFilters {
  type?: ScheduleRuleType;
  chatBotBlockoutId?: string;
  staffId?: string;
  roomId?: string;
  isEnabled?: boolean;
}

export interface CreateScheduleRuleInput {
  type: ScheduleRuleType;
  repeatPattern?: ScheduleRepeatPattern;
  isAllDay?: boolean;
  isEnabled?: boolean;
  chatBotBlockoutId?: string;
  locationId?: string;
  daysOfWeek?: DayOfWeek[];
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  startAtUtc?: string;
  endAtUtc?: string;
  roomId?: string;
  staffId?: string;
  scheduleStatus?: StaffScheduleStatus;
}

export type UpdateScheduleRuleInput = Partial<CreateScheduleRuleInput>;

export interface CalendarEvent {
  id: string;
  startAtUtc: string;
  endAtUtc: string;
  roomId?: string;
  staffId?: string;
  staffIds?: string[];
  assetId?: string;
  assetIds?: string[];
  contactId?: string;
  appointmentTypeId?: string;
  recurringPatternId?: string;
  recurringSeriesId?: string;
  recurrence?: CalendarRecurrence;
  status: CalendarEventStatus;
  title?: string;
  notes?: string;
  allowOverlap?: boolean;
  visitPlace?: FieldVisitPlace;
  createdAtUtc?: string;
  updatedAtUtc?: string;
}

export interface CalendarEventListFilters {
  fromUtc?: string;
  toUtc?: string;
  roomId?: string;
  staffId?: string;
  status?: CalendarEventStatus;
}

export interface CreateCalendarEventInput {
  startAtUtc: string;
  endAtUtc?: string;
  durationMinutes?: number;
  allowOverlap?: boolean;
  roomId?: string;
  staffId?: string;
  staffIds?: string[];
  assetId?: string;
  assetIds?: string[];
  contactId?: string;
  appointmentTypeId?: string;
  recurringPatternId?: string;
  recurringSeriesId?: string;
  recurrence?: CalendarRecurrence;
  status: CalendarEventStatus;
  title?: string;
  notes?: string;
  visitPlace?: FieldVisitPlace;
}

export type UpdateCalendarEventInput = Partial<CreateCalendarEventInput> & {
  applyToSeries?: boolean;
};

export interface AvailabilitySlot {
  startAtUtc: string;
  endAtUtc: string;
  roomId?: string;
  staffId?: string;
}

export interface AvailabilityQuery {
  fromUtc: string;
  toUtc?: string | null;
  slotMinutes?: number;
  durationMinutes?: number;
  roomId?: string;
  staffId?: string;
  appointmentTypeId?: string;
  bookingChannel?: 'aiAssistant' | 'general';
  page?: number;
  pageSize?: number;
}

export interface AvailabilityResult {
  fromUtc: string;
  toUtc?: string;
  slotMinutes: number;
  durationMinutes: number;
  roomId?: string;
  staffId?: string;
  page: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  slots: AvailabilitySlot[];
}

/**
 * Tenant-wide switches for the constraints applied while listing available slots.
 * Every constraint is honoured unless an admin turns it off.
 */
export interface CalendarAvailabilitySettings {
  respectOpeningHours: boolean;
  respectClosures: boolean;
  respectRoomBlockouts: boolean;
  respectBookingPreferenceRules: boolean;
  respectStaffShifts: boolean;
  respectStaffTimeOff: boolean;
  respectExistingBookings: boolean;
  chatBotEmployeeSelection: ChatBotEmployeeSelection;
}

export type ChatBotEmployeeSelection = 'off' | 'optional' | 'required';

export const CHATBOT_EMPLOYEE_SELECTION_MODES: readonly ChatBotEmployeeSelection[] = [
  'off',
  'optional',
  'required',
];

export type CalendarAvailabilitySettingKey = Exclude<
  keyof CalendarAvailabilitySettings,
  'chatBotEmployeeSelection'
>;

export const CALENDAR_AVAILABILITY_SETTING_KEYS: readonly CalendarAvailabilitySettingKey[] = [
  'respectOpeningHours',
  'respectClosures',
  'respectRoomBlockouts',
  'respectBookingPreferenceRules',
  'respectStaffShifts',
  'respectStaffTimeOff',
  'respectExistingBookings',
];

export interface Employee {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
}

export interface AppointmentType {
  id: string;
  name: string;
  description?: string;
  defaultDurationMinutes: number;
  color?: string;
  status: AppointmentTypeStatus;
  consultationCreationMode: ConsultationCreationMode;
  chatBotBookingFlow: ChatBotBookingFlow;
  chatBotInstruction?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AppointmentTypeListFilters {
  status?: AppointmentTypeStatus;
}

export interface CreateAppointmentTypeInput {
  name: string;
  defaultDurationMinutes: number;
  status: AppointmentTypeStatus;
  description?: string;
  color?: string;
  consultationCreationMode?: ConsultationCreationMode;
  chatBotBookingFlow?: ChatBotBookingFlow;
  chatBotInstruction?: string;
  sortOrder?: number;
}

export interface UpdateAppointmentTypeInput {
  name?: string;
  defaultDurationMinutes?: number;
  status?: AppointmentTypeStatus;
  description?: string;
  color?: string;
  consultationCreationMode?: ConsultationCreationMode;
  chatBotBookingFlow?: ChatBotBookingFlow;
  chatBotInstruction?: string;
  sortOrder?: number;
}

export interface Consultation {
  id: string;
  eventId: string;
  assetId: string;
  contactId?: string;
  anamnesisSummary?: string;
  notes?: string;
  moduleKey?: string;
  domainProfileKey?: string;
  templateId?: string;
  status?: string;
  priority?: string;
  channel?: string;
  subjectType?: string;
  subjectId?: string;
  customerContactId?: string;
  assignedEmployeeId?: string;
  calendarEventId?: string;
  taskId?: string;
  conversationId?: string;
  externalReference?: string;
  scheduledAtUtc?: string;
  startedAtUtc?: string;
  completedAtUtc?: string;
  summary?: string;
  internalNotes?: string;
  outcomeType?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConsultationListFilters {
  eventId?: string;
  assetId?: string;
  contactId?: string;
  includeDeleted?: boolean;
}

export interface CreateConsultationInput {
  eventId?: string;
  assetId?: string;
  contactId?: string;
  anamnesisSummary?: string;
  notes?: string;
  moduleKey?: string;
  domainProfileKey?: string;
  templateId?: string;
  status?: string;
  priority?: string;
  channel?: string;
  subjectType?: string;
  subjectId?: string;
  customerContactId?: string;
  assignedEmployeeId?: string;
  calendarEventId?: string;
  taskId?: string;
  conversationId?: string;
  externalReference?: string;
  scheduledAtUtc?: string;
  startedAtUtc?: string;
  completedAtUtc?: string;
  summary?: string;
  internalNotes?: string;
  outcomeType?: string;
}

export type UpdateConsultationInput = Partial<CreateConsultationInput>;

export interface ConsultationTemplateField {
  id: string;
  templateId: string;
  fieldDefinitionId: string;
  sectionKey?: string;
  sortOrder: number;
  isRequired: boolean;
  createdAt: string;
}

export interface ConsultationTemplate {
  id: string;
  name: string;
  code?: string;
  domainProfileKey: string;
  subjectType?: string;
  description?: string;
  defaultStatus: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  fields?: ConsultationTemplateField[];
}

export interface ConsultationTemplateListFilters {
  domainProfileKey?: string;
  subjectType?: string;
  isActive?: boolean;
  search?: string;
}

export interface ConsultationFieldDefinition {
  id: string;
  name: string;
  fieldType: ConsultationFieldType;
  description?: string;
  isRequired: boolean;
  defaultValue?: string;
  options?: ConsultationFieldOption[];
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConsultationFieldDefinitionListFilters {
  fieldType?: ConsultationFieldType;
  isActive?: boolean;
}

export interface ConsultationFieldValue {
  id: string;
  consultationId: string;
  fieldDefinitionId: string;
  value?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddConsultationFieldValueInput {
  fieldDefinitionId: string;
  value?: string;
  sortOrder?: number;
}

export interface UpdateConsultationFieldValueInput {
  value?: string;
  sortOrder?: number;
}
